import { useState, useEffect } from 'react';
import type { Combo } from '@mtg-deck-tool/shared';
import { api } from '../lib/api-client.js';

export interface UseCardCombosResult {
  combos: Combo[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * Fetches combo variants for a card from Commander Spellbook.
 * Returns up to 5 combos. On error, returns an empty array silently.
 *
 * @param cardName - The exact card name to look up combos for.
 * @returns Object with combos, isLoading, and isError state.
 */
export function useCardCombos(cardName: string): UseCardCombosResult {
  const [combos, setCombos] = useState<Combo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!cardName) return;

    let cancelled = false;
    setIsLoading(true);
    setIsError(false);

    api
      .getCardCombos(cardName)
      .then((response) => {
        if (!cancelled) {
          setCombos(response.combos);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCombos([]);
          setIsError(true);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [cardName]);

  return { combos, isLoading, isError };
}
